import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProcessDefinitionHeader } from './ProcessDefinitionHeader';
import { ProcessArtifactGroups } from './ProcessArtifactGroups';
import { ProcessValidationPanel } from './ProcessValidationPanel';
import { ProcessNavigationPanel } from './ProcessNavigationPanel';
import { ProcessDefinitionService } from '../../services/processDefinitionService';
import {
  AddArtifactData,
  ProcessDefinition,
  ProcessValidationResult,
  ProcessWithTransitions,
} from '../../types/processDefinition';
import './ProcessDefinitionPage.css';

export const ProcessDefinitionPage: React.FC = () => {
  const { processId } = useParams<{ processId: string }>();
  const navigate = useNavigate();

  const [processDefinition, setProcessDefinition] = useState<ProcessDefinition | null>(null);
  const [validationResults, setValidationResults] = useState<ProcessValidationResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [showNavigation, setShowNavigation] = useState(false);

  const loadProcessDefinition = useCallback(async () => {
    if (!processId) return;

    try {
      setLoading(true);
      setError(null);
      const data: ProcessWithTransitions =
        await ProcessDefinitionService.getProcessWithTransitions(processId);
      // Merge the process and transitions into a single ProcessDefinition object
      const processWithTransitions: ProcessDefinition = {
        ...data.process,
        transitions: data.transitions,
      };
      setProcessDefinition(processWithTransitions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load process definition');
    } finally {
      setLoading(false);
    }
  }, [processId]);

  const loadValidationResults = useCallback(async () => {
    if (!processId) return;

    try {
      const results = await ProcessDefinitionService.getProcessValidation(processId);
      setValidationResults(results);
    } catch (err) {
      console.error('Failed to load validation results:', err);
    }
  }, [processId]);

  useEffect(() => {
    if (processId) {
      void loadProcessDefinition();
      void loadValidationResults();
    }
  }, [processId, loadProcessDefinition, loadValidationResults]);

  const handleProcessUpdate = async (updatedProcess: Partial<ProcessDefinition>) => {
    if (!processDefinition) return;

    try {
      // Update process parameters
      const updated: ProcessWithTransitions = await ProcessDefinitionService.updateProcess(
        processDefinition.id,
        updatedProcess
      );
      // Merge the process and transitions into a single ProcessDefinition object
      const processWithTransitions: ProcessDefinition = {
        ...updated.process,
        transitions: updated.transitions,
      };
      setProcessDefinition(processWithTransitions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update process');
    }
  };

  const handleAddArtifact = async (artifactData: Record<string, unknown>) => {
    if (!processDefinition) return;

    try {
      // Create new transition (add artifact to process)
      await ProcessDefinitionService.addArtifactToProcess(
        processDefinition.id,
        artifactData as unknown as AddArtifactData
      );
      await loadProcessDefinition(); // Reload to get updated data
      await loadValidationResults(); // Reload validation
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add artifact to process');
    }
  };

  const handleUpdateTransition = async (
    transitionId: string,
    transitionData: Record<string, unknown>
  ) => {
    try {
      await ProcessDefinitionService.updateTransition(transitionId, transitionData);
      await loadProcessDefinition(); // Reload to get updated data
      await loadValidationResults(); // Reload validation
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update transition');
    }
  };

  const handleDeleteTransition = async (transitionId: string) => {
    try {
      await ProcessDefinitionService.deleteTransition(transitionId);
      await loadProcessDefinition(); // Reload to get updated data
      await loadValidationResults(); // Reload validation
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete transition');
    }
  };

  const handleNavigateToProcess = (targetProcessId: string) => {
    navigate(`/process-definition/${targetProcessId}`);
  };

  if (loading) {
    return (
      <div className="process-definition-page">
        <div className="loading-spinner">Loading process definition...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="process-definition-page">
        <div className="error-message">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={loadProcessDefinition}>Retry</button>
        </div>
      </div>
    );
  }

  if (!processDefinition) {
    return (
      <div className="process-definition-page">
        <div className="not-found">
          <h2>Process Not Found</h2>
          <p>The requested process could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="process-definition-page">
      <ProcessDefinitionHeader
        process={processDefinition}
        onProcessUpdate={handleProcessUpdate}
        validationResults={validationResults}
        onToggleValidation={() => setShowValidation(!showValidation)}
        onToggleNavigation={() => setShowNavigation(!showNavigation)}
      />

      <div className="process-definition-content">
        <div className="main-content">
          <ProcessArtifactGroups
            process={processDefinition}
            onAddArtifact={handleAddArtifact as unknown as (artifactData: AddArtifactData) => void}
            onUpdateTransition={handleUpdateTransition}
            onDeleteTransition={handleDeleteTransition}
            onNavigateToProcess={handleNavigateToProcess}
          />
        </div>

        {showValidation && (
          <div className="sidebar-panel">
            <ProcessValidationPanel
              validationResults={validationResults}
              onRefresh={loadValidationResults}
            />
          </div>
        )}

        {showNavigation && (
          <div className="sidebar-panel">
            <ProcessNavigationPanel
              process={processDefinition}
              onNavigateToProcess={handleNavigateToProcess}
            />
          </div>
        )}
      </div>
    </div>
  );
};
